import { useCallback, useLayoutEffect, useRef } from "react";
import { useMotionValue, useTransform, useMotionValueEvent, animate } from "framer-motion";

// Tinder-grade swipe physics, shared by every swipeable card in the app
// (landing hero, /swipe deck, /map poster + docked deck, renter browse,
// and the expanded profile popup). Numbers follow docs/TINDER-UX-SPEC.md:
//
// - 1:1 pointer tracking in x AND y, zero easing during a drag (motion
//   values only, no React state per frame).
// - Rotation: +-18deg when the horizontal drag equals the viewport width,
//   pivot flipped when the card is grabbed in its lower half; a committed
//   exit keeps rotating up to +-36deg.
// - Commit: distance >= 25% of min(vw, vh) OR velocity >= 500 px/s.
// - Exit: linear, along the user's drag vector, duration = clamp(500/|v|,
//   0.15, 0.7)s for flicks and a flat 0.7s for slow drags and buttons.
// - Snap-back: underdamped spring { duration: 0.6, bounce: 0.5 } with a
//   visible overshoot; grabbing the card mid spring must pick it up from
//   its current position (all running animations stop on pointer down).
// - Stamps hit FULL opacity exactly at the commit threshold; button swipes
//   fade the stamp in over the first ~105ms of the 0.7s exit.
// - The onSwipe callback fires at animation START (optimistic); a parent
//   gate can return false to veto, and the card springs home instead.
// - onExited fires when the fly-out ANIMATION completes — the persistent
//   card stack unmounts the card only then, so the deck advances under a
//   card that is still visibly flying (no jump, no remount).
// - enterFrom (+1 / -1) reverse-swipes the card in from off screen along
//   its exit direction in 0.25s — undo and backend vetoes use it.

const SWIPE_VELOCITY = 500; // px/s — flick commit threshold
const MAX_ROTATION = 18; // deg at drag == viewport width; exits reach 36
const EXIT_MIN = 0.15;
const EXIT_MAX = 0.7;
const STAMP_LEAD = 0.105; // button swipes: stamp fades in first (~15% of 0.7s)
const RETURN_SPRING = { type: "spring", duration: 0.6, bounce: 0.5 };

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const vw = () => (typeof window !== "undefined" ? window.innerWidth : 800);
const vh = () => (typeof window !== "undefined" ? window.innerHeight : 600);
const minDim = () => Math.min(vw(), vh());

// The commit distance, shared with stamps so full opacity lands exactly on
// the release-commits point.
export const commitDistance = () => 0.25 * minDim();

export default function useSwipePhysics({ onSwipe, onExited, enabled = true, mirrorX, enterFrom = 0 } = {}) {
  // Undo / rewind entrance: the restored card starts off screen along its
  // old exit direction and reverse-swipes home (read ONCE, at mount).
  const enterRef = useRef(enterFrom);
  const x = useMotionValue(enterRef.current ? Math.sign(enterRef.current) * (vw() * 0.7 + 160) : 0);
  const y = useMotionValue(0);
  // +1 = grabbed the top half (rotate with x), -1 = bottom half (against x).
  const grabDir = useMotionValue(1);
  // Extra rotation applied only during the exit so the card leaves at +-36deg.
  const rotateBoost = useMotionValue(0);
  // Signed 0 -> +-1: button swipes drive the stamp ahead of the translation.
  const stampBoost = useMotionValue(0);
  const pointerEvents = useMotionValue("auto");
  const exitingRef = useRef(false);
  const animsRef = useRef([]);
  // Exit animations are tracked separately: pointer-downs must never stop a
  // committed fly-out, but an undo that re-fronts the card must.
  const exitAnimsRef = useRef([]);
  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  // rotation = clamp(x / vw, -1, 1) * 18deg, flipped by the grab half, plus
  // the exit-only boost toward +-36deg.
  const rotate = useTransform(() => {
    const raw = Math.max(-1, Math.min(1, x.get() / vw())) * MAX_ROTATION;
    return (raw + rotateBoost.get()) * grabDir.get();
  });

  // Stamp opacity ramps 0 -> 1 over exactly the commit distance (NOT the
  // screen edge), so it is fully opaque at the point where a release swipes.
  const likeOpacity = useTransform(() => {
    const fromDrag = Math.min(1, Math.max(0, x.get() / commitDistance()));
    return Math.max(fromDrag, Math.max(0, stampBoost.get()));
  });
  const nopeOpacity = useTransform(() => {
    const fromDrag = Math.min(1, Math.max(0, -x.get() / commitDistance()));
    return Math.max(fromDrag, Math.max(0, -stampBoost.get()));
  });

  // Mirror the drag magnitude into a deck-owned motion value so the card
  // behind can grow 0.95 -> 1.0, reaching 1.0 at minDim / 2 (the deck owns
  // the transform; see the depth-1 tracking in card_stack.jsx).
  const mirrorProgress = () => {
    if (mirrorX && !exitingRef.current) {
      mirrorX.set(Math.max(Math.abs(x.get()), Math.abs(y.get())));
    }
  };
  useMotionValueEvent(x, "change", mirrorProgress);
  useMotionValueEvent(y, "change", mirrorProgress);

  // Interruptibility: every animation we start is tracked so a pointer-down
  // can stop them all and pick the card up mid flight (mid snap-back grabs).
  const stopAnimations = useCallback(() => {
    animsRef.current.forEach((c) => c?.stop?.());
    animsRef.current = [];
  }, []);
  const track = useCallback((...controls) => {
    animsRef.current.push(...controls);
  }, []);

  // Undo entrance: reverse-swipe back to center in 0.25s (spec §2.3 rewind).
  // The stamp is naturally visible at the start (opacity derives from x) and
  // fades as the card lands.
  useLayoutEffect(() => {
    if (!enterRef.current) return;
    track(animate(x, 0, { duration: 0.25, ease: "easeOut" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spring home when the release does not qualify: underdamped, one visible
  // overshoot (spec: duration 0.6, bounce 0.5). Rotation boost and stamp
  // boost ease back over 0.2s linear alongside the spring.
  const springBack = useCallback(() => {
    stopAnimations();
    track(
      animate(x, 0, RETURN_SPRING),
      animate(y, 0, RETURN_SPRING),
      animate(rotateBoost, 0, { duration: 0.2, ease: "linear" }),
      animate(stampBoost, 0, { duration: 0.2, ease: "linear" })
    );
  }, [stopAnimations, track, x, y, rotateBoost, stampBoost]);

  // One-shot "teach the gesture" preview: nudge the top card right ~18% of the
  // commit threshold with the LIKE stamp fading in, HOLD at the peak so the
  // LIKE badge reads, spring back to center, then nudge left with NOPE fading
  // in, hold, spring back. It NEVER commits (the card stays), never advances
  // the deck, and bails the moment the user interacts. Deliberately gentle and
  // readable — roughly double the old durations plus a hold at each peak, so a
  // person clearly sees the LIKE / NOPE stamp before the card eases home
  // (~3.2s total). Respects prefers-reduced-motion (skips entirely). Callers
  // own the run-once guard (fired once per page load on the top card).
  const previewRunningRef = useRef(false);
  const runPreview = useCallback(() => {
    if (previewRunningRef.current || exitingRef.current || prefersReducedMotion()) return;
    // Never override a card the user is already touching / mid animation.
    if (x.get() !== 0 || y.get() !== 0) return;
    previewRunningRef.current = true;
    const nudge = 0.18 * commitDistance();
    const OUT = { duration: 0.7, ease: "easeOut" }; // ease toward the peak
    const BACK = { duration: 0.6, ease: "easeInOut" }; // settle back to center
    const HOLD = 360; // ms dwell at each peak so the badge is readable
    // Sequence via chained promises; each leg is interruptible by a pointer
    // down (stopAnimations clears animsRef, and onPointerDown sets the ref).
    const step = (target, opts) => {
      if (!previewRunningRef.current) return Promise.resolve();
      const ctrl = animate(x, target, opts);
      track(ctrl);
      return ctrl.finished.catch(() => {});
    };
    const hold = (ms) =>
      new Promise((resolve) => {
        if (!previewRunningRef.current) return resolve();
        setTimeout(resolve, ms);
      });
    const done = () => {
      previewRunningRef.current = false;
    };
    step(nudge, OUT) // ease right, LIKE fades in
      .then(() => hold(HOLD)) // dwell on LIKE
      .then(() => step(0, BACK)) // ease home
      .then(() => hold(160)) // brief settle at center
      .then(() => step(-nudge, OUT)) // ease left, NOPE fades in
      .then(() => hold(HOLD)) // dwell on NOPE
      .then(() => step(0, BACK)) // ease home
      .then(done, done);
  }, [track, x, y]);

  // A pointer down cancels the preview so the user is never fighting it.
  const cancelPreview = useCallback(() => {
    previewRunningRef.current = false;
  }, []);

  // Committed exit. Returns true when the fly-out started, false when a
  // parent gate vetoed it (the card springs home instead).
  const flyOut = useCallback(
    (dir, velocity, action, offset) => {
      if (exitingRef.current) return false;
      const act = action || (dir > 0 ? "like" : "pass");
      // Optimistic callback at animation start. A parent gate returns false
      // and the card springs back instead of flying while a modal opens.
      if (onSwipeRef.current?.(act) === false) {
        springBack();
        return false;
      }
      exitingRef.current = true;
      pointerEvents.set("none");
      const w = vw();
      const h = vh();
      // Past the far edge plus roughly a card diagonal, so the card is fully
      // off screen at any rotation.
      const dist = Math.max(w, h) + Math.hypot(w, h) * 0.6;
      // Exit continues along the drag vector; buttons exit horizontally with
      // a tiny random y disturbance so repeated presses never look robotic.
      let ux = Math.sign(dir) || 1;
      let uy = (Math.random() - 0.5) * 0.12;
      if (offset && Math.hypot(offset.x, offset.y) > 4) {
        const n = Math.hypot(offset.x, offset.y);
        ux = offset.x / n;
        uy = offset.y / n;
      }
      // Velocity-scaled duration: a 2000 px/s flick exits in 0.25s, a slow
      // drag past the distance threshold or a button press takes 0.7s.
      const speed = Math.abs(velocity?.x || 0);
      const duration =
        speed >= SWIPE_VELOCITY ? Math.min(EXIT_MAX, Math.max(EXIT_MIN, 500 / speed)) : EXIT_MAX;
      const fly = { duration, ease: "linear" };
      stopAnimations();
      const xCtrl = animate(x, ux * dist, fly);
      exitAnimsRef.current = [
        xCtrl,
        animate(y, uy * dist, fly),
        animate(rotateBoost, MAX_ROTATION * (dir > 0 ? 1 : -1), fly),
      ];
      // The stack unmounts the card only when the flight completes — the
      // deck itself already advanced at fly-out start. A rescue (undo mid
      // flight) flips exitingRef back, which swallows this callback.
      xCtrl.then(() => {
        if (exitingRef.current) onExitedRef.current?.();
      });
      // Button swipes: the stamp reaches full opacity in the first ~105ms,
      // ahead of the translation (drag swipes already have it at 1).
      if (!offset) animate(stampBoost, dir > 0 ? 1 : -1, { duration: STAMP_LEAD, ease: "linear" });
      return true;
    },
    [pointerEvents, springBack, stopAnimations, x, y, rotateBoost, stampBoost]
  );

  // Rescue: a card can be promoted back to the live top while its fly-out is
  // still running (undo, or a backend veto that re-fronts the listing). Stop
  // the exit, pick the card up from wherever it is and spring it home.
  useLayoutEffect(() => {
    if (!enabled || !exitingRef.current) return;
    exitAnimsRef.current.forEach((c) => c?.stop?.());
    exitAnimsRef.current = [];
    exitingRef.current = false;
    pointerEvents.set("auto");
    springBack();
  }, [enabled, pointerEvents, springBack]);

  // Buttons run the SAME exit as a committed drag (pure horizontal, 0.7s).
  // Returns false when a gate vetoed the swipe.
  const triggerSwipe = useCallback(
    (action) => {
      const dir = action === "pass" ? -1 : 1;
      return flyOut(dir, null, action, null);
    },
    [flyOut]
  );

  // Capture the grab point to pick the rotation pivot, and stop any running
  // snap-back so the card is picked up from wherever it currently is.
  const onPointerDown = useCallback(
    (e) => {
      cancelPreview();
      if (!exitingRef.current) stopAnimations();
      const rect = e.currentTarget.getBoundingClientRect();
      grabDir.set(e.clientY < rect.top + rect.height / 2 ? 1 : -1);
    },
    [grabDir, stopAnimations, cancelPreview]
  );

  const onDragEnd = useCallback(
    (_e, info) => {
      const { offset, velocity } = info;
      const flick = Math.abs(velocity.x) >= SWIPE_VELOCITY;
      if (flick || Math.abs(offset.x) >= commitDistance()) {
        // Flicks go where the finger threw them, slow drags follow the offset.
        const dir = flick ? Math.sign(velocity.x) : Math.sign(offset.x) || 1;
        flyOut(dir, velocity, undefined, offset);
      } else {
        springBack();
      }
    },
    [flyOut, springBack]
  );

  return {
    x,
    y,
    rotate,
    likeOpacity,
    nopeOpacity,
    triggerSwipe,
    runPreview,
    onDragEnd,
    // Spread onto the motion.div: 1:1 tracking (dragElastic 1, zero-size
    // constraints), no drag momentum (we own the exit), touch-action none so
    // iOS Safari never fights the horizontal drag.
    style: { x, y, rotate, pointerEvents, touchAction: "none" },
    dragProps: {
      drag: enabled,
      dragConstraints: { left: 0, right: 0, top: 0, bottom: 0 },
      dragElastic: 1,
      dragMomentum: false,
      // dragListener stays ON so the card is grabbable mid spring-back.
      dragListener: true,
      onPointerDown,
      onDragEnd: enabled ? onDragEnd : undefined,
    },
  };
}
